import { navigateStage, isFunction } from "../utils";
import DropinSupervisorBase from "../dropin-supervisor-base";
import TimingInfo from "../timing-info";
import { instance } from "@awayfl/swf-loader/dist/lib/factories/base/utilities/Telemetry";

declare global {
  interface Window {
    createjs: typeof createjs;
    stage: createjs.Stage;
  }
}

// this will allow for any child
type StageSymbol = createjs.MovieClip & {
  [key: string]: any;
};

/**
 * @category Animate Proxys
 * @description Baseclass for animate proxys - maps to stage tree
 
*/
export class AnimateProxyBase<
  SymbolType extends createjs.MovieClip = StageSymbol
> {
  protected _stageSymbol: SymbolType = null;
  protected _safeStageSymbol: createjs.MovieClip = null;
  protected _parentStageObject: createjs.MovieClip;
  protected extraData: any = null;
  protected stageSymbolName: string;

  get timingInfo(): TimingInfo {
    return DropinSupervisorBase.singleton.timingInfo;
  }

  get supervisor(): DropinSupervisorBase {
    return DropinSupervisorBase.singleton;
  }

  get stageSymbol() {
    return this._stageSymbol;
  }

  /**
    @description the safe stage symbol is actually a proxy around the real stage
     symbol that knows how to search for things and accomodate
     various animate quirks, specifically random suffixing of symbols
     we want this separate from the primary one as this functionality is greedy
     and can very easily smooth over things that shouldn't actually work in the first place, 
     so it should only be used where the problem arises
     */
  get safeStageSymbol() {
    return this._safeStageSymbol;
  }

  get parentStageObject() {
    return this._stageSymbol;
  }

  get exportRoot() {
    return;
  }

  /**
   *
   * @param symbolPath pass a movie clip object OR relative string path.
   * @param parentSymbol defaults to the window.exportRoot if nothing is passed. allows for relative paths.
   * @param extraData utility object for passing additional data to the proxy object
   */
  constructor(
    symbolPath: string | string[] | object,
    parentSymbol?: any,
    extraData?: any
  ) {
    if (!parentSymbol)
      parentSymbol = window["exportRoot"] as createjs.MovieClip;

    // if an object is passed then
    if (
      symbolPath !== null && // null typeofs to object
      typeof symbolPath == "object"
    ) {
      // they've passed in an object
      // eventually it would be nice to be able to type these
      // if they pass in a symbol
      this._stageSymbol = symbolPath as SymbolType;
      // get the full path
      this.stageSymbolName = this._stageSymbol.name;
    } else {
      if (typeof symbolPath == "string") {
        this.stageSymbolName = symbolPath;
      } else if (symbolPath instanceof Array) {
        this.stageSymbolName = symbolPath.join(".");
      }
      this._stageSymbol = navigateStage(
        <string | string[]>symbolPath,
        parentSymbol
      );
    }

    const symbol = this._stageSymbol;

    // this is for the safe stage symbol - randomly animate adds a suffix and it breaks everything
    // this (javascript) proxy wraps the stage symbol and automatically searches for the missing prop_number
    const getterFunction = function (target, prop, receiver) {
      // if we dont find the prop then regex search for its name with a number suffix
      if (!target[prop]) {
        let keys = Object.keys(target);
        for (let i = 0; i < keys.length; i++) {
          const matches = keys[i].match(new RegExp(`${prop}_\\d+`));
          if (matches) {
            return target[matches[0]];
          }
        }

        return undefined;
      }

      return target[prop];
    };

    this._safeStageSymbol = new Proxy(symbol, {
      get: getterFunction,
    });

    // in theory this cast will always be correct as long as we're exporting from animate
    this._parentStageObject = this._stageSymbol.parent as createjs.MovieClip;

    this.extraData = extraData;

    DropinSupervisorBase.singleton.registerProxyForUpdates(this);

    // now that we've inited,
  }

  public get createjs(): typeof createjs {
    return window.createjs;
  }

  onFrameZero() {}

  // =================================
  //          STAGE HELPERS
  // =================================

  // the root stage object is just a convenient way to access
  // children of another object
  getStageObject(name: string | string[]): any {
    return navigateStage(name, this.stageSymbol);
  }

  fromStageObject(
    path: string | string[],
    callback: (exportRoot: any) => void
  ): any {
    // now that weve navigated, call back in!
    callback(this.navigateStage(path));
  }

  getProxy<ProxyClass extends AnimateProxyBase = AnimateProxyBase>(
    path: string | string[] | object,
    proxyClass?: {
      new (
        path: string | string[] | object,
        parentSymbol?: any,
        extraData?: any
      ): ProxyClass;
    },
    extraData: any = null
  ): ProxyClass {
    let baseProxy;
    if (proxyClass) {
      baseProxy = new proxyClass(path, this._stageSymbol, extraData);
    } else {
      baseProxy = new AnimateProxyBase(path, this._stageSymbol, extraData);
    }
    // let handler = {
    //     get: (target: any, prop: any , receiver: any) => {

    //         console.log('target', target)
    //         if(target[prop]) return target[prop];

    //         if(target.stageSymbol[prop]) {
    //             if(isFunction(target.stageSymbol[prop])) return target.stageSymbol[prop].bind(target.stageSymbol);
    //             return target.stageSymbol[prop];
    //         }

    //         return Reflect.get(target, prop, receiver);
    //     }
    // }

    baseProxy.onFrameZero();

    return baseProxy;
  }

  navigateStage(path: string | string[]) {
    return navigateStage(path, this.stageSymbol);
  }

  onRequestAnimFrame(timingInfo: TimingInfo) {}
}

AnimateProxyBase["Version"] = "1.0.0";

export default AnimateProxyBase;
